import { createContext, useContext, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import styles from './PageTitle.module.css';

/**
 * El título de una pantalla, que a veces vive en la barra de arriba.
 *
 * La barra superior tiene a la izquierda la búsqueda global. Con ese módulo
 * apagado se quedaba vacía: una franja en blanco encima de cada pantalla, y
 * debajo el título ocupando su propio renglón —lo reportó JC el 7 oct 2026—.
 * Con la búsqueda apagada el título sube a ese hueco.
 *
 * **Es el mismo `h1`, no una copia.** Lo sigue escribiendo cada pantalla, con
 * su texto —el saludo del Inicio, «Calendario del área Tecnología»—, y un portal
 * lo pinta en la barra. Una copia sacada del menú habría dicho «Inicio» donde la
 * pantalla dice «Hola, Marco», y serían dos sitios nombrando la misma pantalla.
 *
 * Fuera del shell no hay barra —una prueba, una vista previa— y el título se
 * queda donde estaba, con la clase de su pantalla.
 */

export interface PageTitleSlot {
  /** Si el título va en la barra: la búsqueda global está apagada. */
  readonly inBar: boolean;
  /** El hueco de la barra. `null` hasta que el shell lo monta. */
  readonly node: HTMLElement | null;
}

export const PageTitleSlotContext = createContext<PageTitleSlot>({ inBar: false, node: null });

interface PageTitleProps {
  /** La clase del título **en la página**. En la barra lleva la suya. */
  readonly className?: string | undefined;
  readonly children: ReactNode;
}

export const PageTitle = ({ className, children }: PageTitleProps): ReactNode => {
  const slot = useContext(PageTitleSlotContext);

  if (!slot.inBar) return <h1 className={className}>{children}</h1>;

  /*
   * El hueco existe un instante después que la pantalla: el shell lo monta en el
   * mismo render y lo entrega en el siguiente. Mientras, nada — pintar el título
   * en la página para quitarlo enseguida sería un salto de todo el contenido.
   */
  if (slot.node === null) return null;

  return createPortal(<h1 className={styles.inBar}>{children}</h1>, slot.node);
};
